import type { BootstrapResult, RuntimeApi } from './runtime-bootstrap.js'

import { discoverLiveApi } from '../api-discovery/browser-discovery.js'
import { createIndexedDbTokenStore, resolveIndexedDb } from '../auth/token-store.js'
import { persistApiDomain } from './api-domain-persistence.js'
import { loadApiIndex, persistApiIndex } from './last-known-index.js'
import { bootstrapRuntimeApi } from './runtime-bootstrap.js'
import { setRuntimeStatus } from './runtime-state.js'

/**
 * Module-scope holder for the adopted runtime API. It stays `null` until boot-time discovery
 * has verified a domain against the pinned key — nothing in the app may talk to an API before
 * then. Kept out of React state on purpose: discovery runs once at launch, before (and
 * independently of) any component, and later API call sites read it directly.
 */
let currentRuntimeApi: null | RuntimeApi = null

/**
 * The single in-flight (or settled) boot promise. Memoised so discovery runs exactly once no
 * matter how many callers ask for it, and so a caller can `await` the settled outcome instead of
 * polling the sync {@link getRuntimeApi} getter — which returns `null` throughout the multi-second
 * discovery window and gives no way to tell "still discovering" from "discovery failed".
 */
let bootstrapPromise: null | Promise<BootstrapResult> = null

/**
 * The adopted, signature-verified runtime API, or `null` if discovery has not (yet) succeeded.
 * Synchronous for call sites that already gate on the `runtime-state` status store; a caller with
 * no such gate should await {@link startRuntimeBootstrap} instead, since this returns `null`
 * throughout the discovery window rather than blocking.
 */
export const getRuntimeApi = (): null | RuntimeApi => currentRuntimeApi

/**
 * Run the production boot-time discovery: resume the DGA window from the last-known-good index
 * (PT-5628), probe each candidate with a bounded per-probe timeout (PT-5626), adopt the first one
 * whose signed beacon verifies against the pinned ed25519 key, and expose an
 * `authorizedFetch`-backed API for it. On success the adopted API is cached in this module for
 * later call sites and its index is persisted so the next cold start resumes from it; on failure
 * the boot resolves to a defined `unavailable` state (never a hang).
 */
const runBootstrap = async (): Promise<BootstrapResult> => {
  // A sandboxed in-app/WebView browser can make even *reading* the `indexedDB` global throw. With
  // no store to hold the bearer token the app cannot carry a session, so settle on the defined
  // `unavailable` state (a retry affordance) rather than letting the throw escape boot and strand
  // the shell on its 'discovering' state forever — the sibling localStorage reads are guarded the
  // same way (`last-known-index.ts`, `api-domain-persistence.ts`).
  const tokenStorage = resolveIndexedDb()
  if (!tokenStorage) {
    setRuntimeStatus('unavailable')
    return { status: 'unavailable' }
  }

  // Resume the discovery window from the last domain that earned trust. The DGA pointer only ever
  // advances (a burned domain is never reused), so a start index we previously verified is at or
  // before the current live index — scanning forward from it reaches the live domain while
  // skipping the dead early candidates' per-probe timeouts. A missing/blocked/corrupted value
  // yields `undefined`, which lets `discoverLiveApi` apply its own default (full scan from 0),
  // exactly like a first-ever visit.
  const startIndex = loadApiIndex() ?? undefined
  const result = await bootstrapRuntimeApi({
    store: createIndexedDbTokenStore(tokenStorage),
    discover: () => discoverLiveApi(startIndex),
    fetch: (...args) => fetch(...args),
  })

  if (result.status === 'ready') {
    currentRuntimeApi = result.api
    // Hand the adopted host to the same-origin dashboard (/app). Discovery lives only here.
    persistApiDomain(result.api.apiDomain)
    // Remember where we landed so the next cold start resumes from it instead of re-scanning from 0.
    persistApiIndex(result.api.index)
  }

  // Publish the settled status so the React shell can leave its discovering state and show the
  // login form (ready) or a retry affordance (unavailable). The adopted api is cached above for
  // call sites; the status store is what components subscribe to.
  setRuntimeStatus(result.status === 'ready' ? 'ready' : 'unavailable')

  return result
}

/**
 * Kick off (or, on a repeat call, re-attach to) boot-time API-domain discovery and return the
 * awaitable boot outcome. Idempotent by memoisation: the underlying discovery runs exactly once
 * per launch and every caller shares the one promise, so this doubles as the awaitable handle for
 * anyone who needs the settled result without racing the discovery window.
 */
export const startRuntimeBootstrap = (): Promise<BootstrapResult> => {
  bootstrapPromise ??= runBootstrap()
  return bootstrapPromise
}

/**
 * Reset the memoised boot state. For tests only — production discovers exactly once per launch and
 * never tears the adopted API back down.
 */
export const resetRuntimeApiForTests = (): void => {
  currentRuntimeApi = null
  bootstrapPromise = null
}
